import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Settings, HelpCircle, LogOut, Loader2, CreditCard, Sparkles, ExternalLink } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { GreenLightLogo } from "@/components/GreenLightLogo";
import ProjectsGrid from "@/components/dashboard/ProjectsGrid";
import { CreateProjectModal } from "@/components/dashboard/CreateProjectModal";
import { RenameProjectModal } from "@/components/dashboard/RenameProjectModal";
import { ConfirmationDialog } from "@/components/dashboard/ConfirmationDialog";
import { WelcomeModal } from "@/components/dashboard/WelcomeModal";
import { useProjects, Project } from "@/hooks/useProjects";
import {
  useCreateProject,
  useRenameProject,
  useArchiveProject,
  useRestoreProject,
  useDeleteProject,
} from "@/hooks/useProjectMutations";
import { useAuth } from "@/hooks/useAuth";
import { useSubscription } from "@/hooks/useSubscription";
import { useProfile } from "@/hooks/useProfile";
import { SUBSCRIPTION_TIERS, canCreateProject } from "@/lib/subscription";

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut } = useAuth();

  const handleContactUsClick = () => {
    if (location.pathname === '/help') {
      const element = document.getElementById('contact-us');
      element?.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/help');
      setTimeout(() => {
        const element = document.getElementById('contact-us');
        element?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  };
  const { tier, subscribed, openCustomerPortal, isLoading: subscriptionLoading } = useSubscription();
  const { profile, markWelcomeModalShown } = useProfile();
  
  // Data fetching
  const { data: projects = [], isLoading, error } = useProjects();

  // Welcome modal state
  const [welcomeModalOpen, setWelcomeModalOpen] = useState(false);

  // Show welcome modal for first-time users
  useEffect(() => {
    if (profile && !profile.welcome_modal_shown && !subscriptionLoading) {
      setWelcomeModalOpen(true);
    }
  }, [profile, subscriptionLoading]);
  
  // Mutations
  const createProject = useCreateProject();
  const renameProject = useRenameProject();
  const archiveProject = useArchiveProject();
  const restoreProject = useRestoreProject();
  const deleteProject = useDeleteProject();

  // Get user initials
  const getUserInitials = () => {
    const firstName = user?.user_metadata?.first_name || "";
    const lastName = user?.user_metadata?.last_name || "";
    if (firstName && lastName) {
      return `${firstName[0]}${lastName[0]}`.toUpperCase();
    }
    if (user?.email) {
      return user.email[0].toUpperCase();
    }
    return "U";
  };

  const getGreeting = () => {
    const firstName = user?.user_metadata?.first_name;
    if (firstName) {
      return `Welcome back, ${firstName}`;
    }
    return "Welcome back";
  };

  const handleSignOut = async () => {
    await signOut();
    navigate("/auth", { replace: true });
  };

  // Modal states
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [projectToRename, setProjectToRename] = useState<{ id: string; name: string } | null>(null);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [selectedProjectName, setSelectedProjectName] = useState<string>("");

  const handleCreateProject = (name: string) => {
    createProject.mutate(name);
  };

  // Welcome modal handlers
  const handleWelcomeModalClose = (open: boolean) => {
    if (!open) {
      markWelcomeModalShown();
    }
    setWelcomeModalOpen(open);
  };

  const handleWelcomeCreateProject = () => {
    markWelcomeModalShown();
    setWelcomeModalOpen(false);
    setCreateModalOpen(true);
  };

  const handleRenameProject = (id: string, newName: string) => {
    renameProject.mutate({ id, name: newName });
  };

  const openRenameModal = (id: string) => {
    const project = projects.find((p: Project) => p.id === id);
    if (project) {
      setProjectToRename({ id: project.id, name: project.name });
      setRenameModalOpen(true);
    }
  };

  const openArchiveDialog = (id: string) => {
    const project = projects.find((p: Project) => p.id === id);
    if (project) {
      setSelectedProjectId(id);
      setSelectedProjectName(project.name);
      setArchiveDialogOpen(true);
    }
  };

  const openDeleteDialog = (id: string) => {
    const project = projects.find((p: Project) => p.id === id);
    if (project) {
      setSelectedProjectId(id);
      setSelectedProjectName(project.name);
      setDeleteDialogOpen(true);
    }
  };

  const handleArchiveProject = () => {
    if (selectedProjectId) {
      archiveProject.mutate(selectedProjectId);
    }
  };

  const handleRestoreProject = (id: string) => {
    restoreProject.mutate(id);
  };

  const handleDeleteProject = () => {
    if (selectedProjectId) {
      deleteProject.mutate(selectedProjectId);
    }
  };

  const activeProjectsCount = projects.filter((p: Project) => p.status === "active").length;
  const tierConfig = SUBSCRIPTION_TIERS[tier];
  const projectLimit = tierConfig.projectLimit;
  const canCreate = canCreateProject(activeProjectsCount, tier);

  return (
    <div className="min-h-screen bg-white grain">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-foreground/5">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo - clickable to dashboard */}
            <button 
              onClick={() => navigate('/dashboard')} 
              className="flex items-center gap-2"
            >
              <GreenLightLogo width={190} animate={false} />
            </button>

            <div className="flex items-center gap-6">
              {/* Help Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-1 text-sm font-medium text-foreground/70 hover:text-foreground transition-colors">
                    <HelpCircle className="h-4 w-4" />
                    Help
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem className="cursor-pointer" onClick={() => navigate('/help')}>
                    How to Use
                  </DropdownMenuItem>
                  <DropdownMenuItem className="cursor-pointer" onClick={handleContactUsClick}>
                    Contact Us
                  </DropdownMenuItem>
                  <DropdownMenuItem className="cursor-pointer" onClick={() => window.open('https://form.typeform.com/to/DU5hRCud', '_blank')}>
                    Provide Feedback
                    <ExternalLink className="ml-auto h-3 w-3 text-muted-foreground" />
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Avatar Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="rounded-full focus:outline-none ring-2 ring-accent ring-offset-2">
                    <Avatar className="h-9 w-9 cursor-pointer">
                      <AvatarFallback className="bg-foreground text-background font-medium">{getUserInitials()}</AvatarFallback>
                    </Avatar>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>My Account</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="cursor-pointer" onClick={() => navigate('/plans')}>
                    <Sparkles className="mr-2 h-4 w-4" />
                    Plans
                    {!subscribed && <Badge variant="secondary" className="ml-auto text-xs">Upgrade</Badge>}
                  </DropdownMenuItem>
                  {subscribed && (
                    <DropdownMenuItem className="cursor-pointer" onClick={openCustomerPortal}>
                      <CreditCard className="mr-2 h-4 w-4" />
                      Billing
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem className="cursor-pointer" onClick={() => navigate('/settings')}>
                    <Settings className="mr-2 h-4 w-4" />
                    Settings
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    className="cursor-pointer text-destructive focus:text-destructive"
                    onClick={handleSignOut}
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-8">
        {/* Intro Section */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <h1 className="font-display text-3xl font-bold text-foreground">
              {getGreeting()}
            </h1>
            {!subscriptionLoading && (
              <Badge variant={subscribed ? "default" : "secondary"} className="text-xs">
                {tierConfig.name}
              </Badge>
            )}
          </div>
          <p className="text-muted-foreground">
            {isLoading 
              ? "Loading your projects..." 
              : tier === 'unlimited'
                ? `You have ${activeProjectsCount} active project${activeProjectsCount !== 1 ? 's' : ''}.`
                : `You have ${activeProjectsCount}/${projectLimit} active project${activeProjectsCount !== 1 ? 's' : ''}.`
            }
            {!canCreate && !isLoading && (
              <button 
                onClick={() => navigate('/plans')} 
                className="ml-2 text-link hover:text-link-hover hover:underline font-medium"
              >
                Upgrade for more
              </button>
            )}
          </p>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-accent" />
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <p className="text-destructive mb-2">Failed to load projects</p>
            <p className="text-muted-foreground text-sm">{error.message}</p>
          </div>
        )}

        {/* Projects Grid */}
        {!isLoading && !error && (
          <ProjectsGrid
            projects={projects}
            onCreateProject={() => setCreateModalOpen(true)}
            onRename={openRenameModal}
            onArchive={openArchiveDialog}
            onRestore={handleRestoreProject}
            onDelete={openDeleteDialog}
            canCreateProject={canCreate}
            projectLimit={projectLimit}
            activeProjectCount={activeProjectsCount}
          />
        )}
      </main>

      {/* Create Project Modal */}
      <CreateProjectModal
        open={createModalOpen}
        onOpenChange={setCreateModalOpen}
        onCreateProject={handleCreateProject}
      />

      {/* Rename Project Modal */}
      <RenameProjectModal
        open={renameModalOpen}
        onOpenChange={setRenameModalOpen}
        projectId={projectToRename?.id ?? ""}
        currentName={projectToRename?.name ?? ""}
        onRename={handleRenameProject}
      />

      {/* Archive Confirmation Dialog */}
      <ConfirmationDialog
        open={archiveDialogOpen}
        onOpenChange={setArchiveDialogOpen}
        title="Archive Project"
        description={`Are you sure you want to archive "${selectedProjectName}"? You can restore it later from the archived projects section.`}
        confirmLabel="Archive"
        onConfirm={handleArchiveProject}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete Project"
        description={`Are you sure you want to delete "${selectedProjectName}"? This action cannot be undone.`}
        confirmLabel="Delete"
        onConfirm={handleDeleteProject}
        variant="destructive"
      />

      {/* Welcome Modal */}
      <WelcomeModal
        open={welcomeModalOpen}
        onOpenChange={handleWelcomeModalClose}
        tier={tier}
        tierName={tierConfig.name}
        onCreateProject={handleWelcomeCreateProject}
      />
    </div>
  );
};

export default Dashboard;
